import React, { useMemo } from 'react';

import { styled } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ProfileCardTrigger } from '@atlaskit/profilecard';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { type NLPSearchWhoResultCollaboratorDataType } from '../../ai-answer-dialog/types';
import { messages } from '../../messages';
import { useMeaningfulHover } from '../../v2/common/hooks/use-meaningful-hover';
import { KnowledgeCardsRowRefresh } from '../knowledge-cards-row';
import { PersonCard } from '../person-knowledge-card/person-card';
import { KnowledgeCardsIntentType, KnowledgeCardsRowType } from '../types';
import { useTeamsAppLinks } from '../use-teams-app-links';
import { createProfileClient, trimARI } from '../util';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-unsafe-style-overrides
const CardContainer = styled(TooltipPrimitive)({
	width: '214px',
	height: '236px',
});

type KnowledgeCardUserRowProps = {
	item: NLPSearchWhoResultCollaboratorDataType;
	maxItems: number;
	onClickCallback?: () => void;
};

export const KnowledgeCardUserRow = React.memo(
	({ item: user, onClickCallback }: KnowledgeCardUserRowProps) => {
		const { commonAttributes } = useAIAnswerContext();
		const { toUser } = useTeamsAppLinks({
			type: KnowledgeCardsIntentType.PERSON,
			currentUserId: user.accountId || '',
			cloudId: commonAttributes.cloudId,
		});

		const { formatMessage } = useIntl();

		const userUrl = `/people/${trimARI(user.accountId || '')}`;

		const handleUserClick = (e: React.MouseEvent) => {
			if (fg('platform-adopt-teams-nav-config')) {
				toUser(user.accountId || '').onNavigate(e);
			} else {
				window.open(user.url || userUrl, '_self', 'noopener, noreferrer');
			}
		};

		const profileClient = useMemo(
			() => createProfileClient(commonAttributes.cloudId),
			[commonAttributes.cloudId],
		);

		const { handleHoverStart, handleHoverLeave } = useMeaningfulHover({
			elementType: 'collaborator',
			hoverCardSelector: '[data-testid="profilecard"]',
		});

		if (fg('kd_platform_person_cards')) {
			return (
				<ProfileCardTrigger
					userId={trimARI(user.accountId || '')}
					resourceClient={profileClient}
					actions={[
						{
							label: formatMessage(messages.knowledge_cards_person_hover_profile_link),
							link: toUser(user.accountId || '').href,
							callback: (e) => {
								onClickCallback?.();
								toUser(user.accountId || '').onNavigate(e);
							},
						},
					]}
					ariaLabel={user.name}
					position="bottom-end"
				>
					<Box
						onMouseEnter={fg('kd_meaningful_hover') ? handleHoverStart : undefined}
						onMouseLeave={fg('kd_meaningful_hover') ? handleHoverLeave : undefined}
					>
						<KnowledgeCardsRowRefresh
							primaryText={user.name}
							secondaryText={
								!!user.title || !!user.department
									? [user.title, user.department].filter((str) => !!str).join(', ')
									: ''
							}
							pictureUrl={user.picture || ''}
							type={KnowledgeCardsRowType.PERSON}
							onClick={(e: React.MouseEvent<HTMLDivElement>) => {
								onClickCallback?.();
								handleUserClick(e);
							}}
						/>
					</Box>
				</ProfileCardTrigger>
			);
		}

		return (
			<Box
				onMouseEnter={fg('kd_meaningful_hover') ? handleHoverStart : undefined}
				onMouseLeave={fg('kd_meaningful_hover') ? handleHoverLeave : undefined}
			>
				<Tooltip
					key={user.accountId}
					position="bottom-end"
					// @ts-ignore -- `styled(TooltipPrimitive)` is invalid for `component` (ignored as it fails types in Jira and should in Platform)
					component={CardContainer}
					content={
						<PersonCard
							key={user.accountId}
							name={user.name}
							profilePicUrl={user.picture || ''}
							accountId={user.accountId || ''}
							jobTitle={user.title || ''}
							profileUrl={user.url || userUrl}
							email={user.email || ''}
						/>
					}
				>
					{(triggerProps) => (
						<KnowledgeCardsRowRefresh
							primaryText={user.name}
							secondaryText={
								!!user.title || !!user.department
									? [user.title, user.department].filter((str) => !!str).join(', ')
									: ''
							}
							pictureUrl={user.picture || ''}
							type={KnowledgeCardsRowType.PERSON}
							triggerProps={triggerProps}
							onClick={(e: React.MouseEvent<HTMLDivElement>) => {
								onClickCallback?.();
								handleUserClick(e);
							}}
						/>
					)}
				</Tooltip>
			</Box>
		);
	},
);
